import { ServiceOrder, ServiceOrderExtra } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Form } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import Presentation from './LabourTable.presentation';

interface IProps {
  hideAction?: boolean;
  serviceOrders: ServiceOrder[];
  serviceOrderExtras?: ServiceOrderExtra[];
  handleServiceOrderDelete?: (id: string) => void;
  handleServiceOrdersUpdate?: (serviceOrders: ServiceOrder) => void;
}

export const LabourTable: React.FC<IProps> = observer(
  ({
    serviceOrders,
    hideAction = false,
    serviceOrderExtras,
    handleServiceOrderDelete,
    handleServiceOrdersUpdate,
  }) => {
    const { orderStore } = useStore();

    const [form] = Form.useForm();
    const [editingKey, setEditingKey] = useState('');
    const [deleteId, setDeleteId] = useState<string>();
    const [deleteServiceOrderId, setDeleteServiceOrderId] = useState<string | undefined>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isEditing = (record: ServiceOrder) => record.id === editingKey;

    const handleUpdate = useCallback(
      async (key: React.Key) => {
        form.validateFields().then(async (values) => {
          await orderStore
            .updateServiceOrder({ ...values, id: key, duration: values.timeSpent })
            .then((result) => {
              if (result) {
                handleServiceOrdersUpdate && handleServiceOrdersUpdate(result);
              }
              setEditingKey('');
            });
        });
      },
      [form, orderStore, handleServiceOrdersUpdate]
    );

    const handleUpdateExtra = useCallback(
      async (key: React.Key, parentKey?: React.Key) => {
        form.validateFields().then(async (values) => {
          const serviceOrderId = parentKey as string;
          await orderStore
            .updateExtra({
              ...values,
              id: key,
              quantity: values.timeSpent,
              serviceOrderId,
            })
            .then((result) => {
              if (result) {
                handleServiceOrdersUpdate && handleServiceOrdersUpdate(result);
              }
            });

          setEditingKey('');
        });
      },
      [form, orderStore, serviceOrderExtras]
    );

    const handleDelete = useCallback(async () => {
      try {
        if (deleteServiceOrderId && deleteId) {
          await orderStore.deleteExtra({ id: deleteId }, deleteServiceOrderId);
          handleServiceOrderDelete && handleServiceOrderDelete(deleteId);
        } else if (deleteId) {
          await orderStore.deleteServiceOrder({ id: deleteId });
          handleServiceOrderDelete && handleServiceOrderDelete(deleteId);
        }
      } finally {
        setShowDeleteModal(false);
      }
    }, [orderStore, deleteId, deleteServiceOrderId, handleServiceOrderDelete]);

    const handleEdit = (record: Partial<ServiceOrder> & { key: React.Key }) => {
      form.setFieldsValue(record);
      setEditingKey(record.id || '');
    };

    const handleCancel = () => {
      setEditingKey('');
    };

    const handleDeleteClick = (id: string, serviceOrderId?: string) => {
      setDeleteServiceOrderId(serviceOrderId);
      setDeleteId(id);
      setShowDeleteModal(true);
    };

    //This should be handled with useMemo but not rerendering properly, IMPROVE LATER!!!
    const mappedServiceOrders = serviceOrders.map((serviceOrder) => {
      return {
        ...serviceOrder,
        timeSpent: serviceOrder.duration,
        total: Utils.calculateRowTotalPrice(serviceOrder),
        type: serviceOrder.isFixed ? 'Fixed' : 'Labour',
        children:
          serviceOrder.extras.length > 0
            ? serviceOrder.extras.map((extra) => ({
                ...extra,
                key: extra.id,
                title: extra.name,
                timeSpent: extra.quantity,
                isExtra: true,
                total: Utils.calculateRowTotalPrice({
                  ...extra,
                  duration: extra.quantity,
                }),
              }))
            : null,
      };
    });

    return (
      <Presentation
        form={form}
        hideAction={hideAction}
        dataSource={mappedServiceOrders}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleDeleteClick={handleDeleteClick}
        handleCancel={handleCancel}
        handleUpdate={handleUpdate}
        editingKey={editingKey}
        isEditing={isEditing}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleUpdateExtra={handleUpdateExtra}
      />
    );
  }
);
